export const shopName: string = 'shopCart';

export class LocalStorageUtils {

  /**
   * Retrieves the value associated with the specified key from local storage.
   * If the key does not exist, an empty string is returned.
   *
   * @param {string} key - The key to retrieve the value for.
   * @returns {string} - The value associated with the key, or an empty string if the key does not exist.
   */
  static getKeyValue(key: string): string {
    const localStorage: Storage = window.localStorage;

    if (!Object.hasOwn(localStorage, key)) {
      return '';
    }

    const value: string = localStorage[key];

    return value ?? '';
  }

  /**
   * Retrieves a nested configuration value from local storage based on the provided key.
   * The key should be in the format `parentKey.childKey`.
   * If the key does not exist, an empty string is returned.
   *
   * @param {string} envKey - The key to retrieve the value for, formatted as `parentKey.childKey`.
   * @returns {string} - The value associated with the key, or an empty string if the key does not exist.
   */
  static getKeyValueNew(envKey: string): string {
    const localStorage: Storage = window.localStorage;
    const lang: string = LocalStorageUtils.getKeyValue(`lang`);

    let value: string = '';

    if (!Object.hasOwn(localStorage, `config-data-${lang}`)) {
      return '';
    }

    const data: any = JSON.parse(LocalStorageUtils.getKeyValue(`config-data-${lang}`));

    Object.keys(data).forEach(
      key => {
        const neededParentKey: string =  envKey.split(".")[0];
        if (key === neededParentKey) {
          Object.keys(data[key]).forEach(
            deepKey => {
              const neededChildKey: string =  envKey.split(".")[1];
              if (deepKey === neededChildKey) {
                value = data[key][deepKey];
              }
            }
          );
        }
      }
    );

    return value;
  }

  /**
   * Sets a key-value pair in local storage.
   * If the key already exists, its value will be updated.
   *
   * @param {string} key - The key to set the value for.
   * @param {string} value - The value to associate with the key.
   */
  static setKeyValue(key: string, value: string): void {
    const localStorage: Storage = window.localStorage;
    localStorage.setItem(key, value);
  }

  /**
   * Clears the value associated with the specified key in local storage.
   * If the key does not exist, no action is taken.
   *
   * @param {string} key - The key to clear the value for.
   */
  static clearKey(key: string): void {
    const localStorage: Storage = window.localStorage;
    localStorage.removeItem(key);
  }

  /**
   * Clears all keys in local storage except those specified in the exclude array.
   *
   * @param {string[]} exclude - An array of keys to exclude from clearing.
   */
  static clearLocalStorage(exclude: string[]): void {
    for (let i: number = 0; i < localStorage.length; i++) {
      const key: string = localStorage.key(i)!;

      if (exclude.indexOf(key) === -1) {
        localStorage.removeItem(key);
      }
    }
  }

  /**
   * Clear all local storage items related to authentication tokens - those tokens are stored with keys containing '@@auth0spajs@@'.
   */
  static clearAuthTokens(): void {
    Object.keys(localStorage).forEach(key => {
      if (key.includes('@@auth0spajs@@')) {
        localStorage.removeItem(key);
      }
    });
  }
}
